// src/clock/JobCheckIn.js
//
// UI for job check-in / check-out. Logic lives in ./jobVisits.js.
//
//   <ActiveJobBanner jobVisits={jobVisits} />          top of Home: current stop + timer
//   <JobCheckInButton job={job} jobVisits={jobVisits} /> on each job card / job popup

import React, { useEffect, useState } from "react";
import moment from "moment";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebaseConfig";
import {
  JOB_VISITS,
  getVisitOccurrenceKey,
  formatElapsed,
  formatMinutes,
  toDateSafe,
  useNow,
  visitMinutes,
  mapsLinkFor,
  FAR_FROM_JOB_METERS,
  describeVisit,
  getCheckInJob,
  isJobDoneForCheckIn,
  isSameJobOccurrence,
  isUserOnJob,
  jobRunsToday,
} from "./jobVisits";
import "./jobCheckIn.css";
import { useLanguage } from "../i18n";

const timeText = (v) => {
  const d = toDateSafe(v);
  return d ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "—";
};

const distanceText = (m) => {
  if (m == null) return null;
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1609.34).toFixed(1)} mi`;
};

// Small tags for how a visit happened: "auto", "outside fence".
const FenceTags = ({ visit }) => {
  const { t } = useLanguage();
  if (!visit) return null;
  return (
    <>
      {visit.checkInMethod === "auto" && (
        <span className="jci-tag auto" title={t("Checked in automatically when they entered the job area")}>
          {t("auto in")}
        </span>
      )}
      {visit.checkInOutsideFence && (
        <span className="jci-tag warn" title={t("Checked in by hand from outside the job area")}>
          {t("outside fence")}{visit.checkInFenceDistanceMeters ? ` +${distanceText(visit.checkInFenceDistanceMeters)}` : ""}
        </span>
      )}
    </>
  );
};

const checkOutReasonText = (v) => {
  switch (v.checkOutReason) {
    case "clock_out":
      return "auto: clocked out";
    case "switched_jobs":
      return "auto: checked in elsewhere";
    case "job_completed":
      return "auto: job completed";
    case "left_fence":
      return v.checkOutTimeEstimated
        ? "auto: left job area (time estimated)"
        : "auto: left job area";
    default:
      return v.checkOutReason;
  }
};

// Running timer for one open visit.
const RunningTimer = ({ visit, className = "" }) => {
  const now = useNow(1000, !!visit);
  const start = toDateSafe(visit?.checkInAt);
  if (!start) return null;
  return <span className={`jci-timer ${className}`}>{formatElapsed(now - start.getTime())}</span>;
};

// ---------------------------------------------------------------------------
export const JobCheckInButton = ({ job, jobVisits, compact = false }) => {
  const { t } = useLanguage();
  if (!job || !jobVisits) return null;

  const { isCheckedInTo, visitsForJob, checkIn, checkOut, isBusyFor, isBusy, openVisit } = jobVisits;
  const here = isCheckedInTo(job);
  const busy = isBusyFor(job);
  const past = visitsForJob(job).filter((v) => v.status === "checked_out");
  const totalMins = past.reduce((sum, v) => sum + visitMinutes(v), 0);

  return (
    <div className={`jci-wrap ${compact ? "compact" : ""}`} onClick={(e) => e.stopPropagation()}>
      {here ? (
        <>
          <div className="jci-status in">
            <span className="jci-dot" /> {t("Checked in {time}", { time: timeText(openVisit.checkInAt) })} ·{" "}
            <RunningTimer visit={openVisit} />
          </div>
          <button
            type="button"
            className="jci-btn out"
            disabled={busy}
            onClick={() => checkOut("user")}
          >
            {busy ? t("Saving…") : t("Check Out")}
          </button>
        </>
      ) : (
        <button
          type="button"
          className="jci-btn in"
          disabled={busy || isBusy}
          onClick={() => checkIn(job)}
          title={t("Records your arrival time and location for this job")}
        >
          {busy ? t("Getting location…") : past.length ? t("Check In Again") : t("Check In")}
        </button>
      )}

      {past.length > 0 && (
        <div className="jci-history">
          {past.length === 1
            ? t("Time on this job: {total} ({from}–{to})", {
                total: formatMinutes(totalMins),
                from: timeText(past[0].checkInAt),
                to: timeText(past[0].checkOutAt),
              })
            : t("Time on this job: {total} over {n} visits", { total: formatMinutes(totalMins), n: past.length })}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
export const ActiveJobBanner = ({ jobVisits, isClockedIn }) => {
  const { t } = useLanguage();
  const [showToday, setShowToday] = useState(false);
  if (!jobVisits) return null;

  const { openVisit, todaysVisits, checkOut, isBusy, error } = jobVisits;
  const now = Date.now();
  const totalToday = todaysVisits.reduce((sum, v) => sum + visitMinutes(v, now), 0);

  return (
    <div className="jci-banner">
      {openVisit ? (
        <div className="jci-banner-row">
          <div className="jci-banner-main">
            <div className="jci-banner-label">
              <span className="jci-dot" /> {t("Checked in at")}
            </div>
            <div className="jci-banner-site">
              {openVisit.siteName || t("Job")}
              {openVisit.wo ? <span className="jci-wo"> WO #{openVisit.wo}</span> : null}
            </div>
            <div className="jci-banner-sub">
              {t("Since {time}", { time: timeText(openVisit.checkInAt) })} <FenceTags visit={openVisit} />
              {!openVisit.fence &&
                openVisit.checkInDistanceMeters != null &&
                openVisit.checkInDistanceMeters > FAR_FROM_JOB_METERS && (
                  <span className="jci-warn">
                    {" "}· {t("{distance} from job address", { distance: distanceText(openVisit.checkInDistanceMeters) })}
                  </span>
                )}
            </div>
          </div>
          <RunningTimer visit={openVisit} className="big" />
          <button
            type="button"
            className="jci-btn out"
            disabled={isBusy}
            onClick={() => checkOut("user")}
          >
            {isBusy ? t("Saving…") : t("Check Out")}
          </button>
        </div>
      ) : (
        <div className="jci-banner-empty">
          {isClockedIn
            ? t("Not checked in to a job. Tap Check In on a job when you arrive.")
            : t("Clock in for the day, then check in at each job when you arrive.")}
        </div>
      )}

      {error && <div className="jci-error">{error}</div>}

      {todaysVisits.length > 0 && (
        <div className="jci-today">
          <button type="button" className="jci-link" onClick={() => setShowToday((v) => !v)}>
            {showToday ? "▾" : "▸"} {t("Today's stops: {n} · {total} on jobs", { n: todaysVisits.length, total: formatMinutes(totalToday) })}
          </button>
          {showToday && (
            <table className="jci-table">
              <thead>
                <tr>
                  <th>{t("Job")}</th>
                  <th>{t("In")}</th>
                  <th>{t("Out")}</th>
                  <th>{t("Time")}</th>
                </tr>
              </thead>
              <tbody>
                {todaysVisits.map((v) => (
                  <tr key={v.id}>
                    <td>
                      {v.siteName || t("Job")}
                      {v.wo ? <div className="jci-muted">WO #{v.wo}</div> : null}
                    </td>
                    <td>{timeText(v.checkInAt)}</td>
                    <td>{v.status === "checked_in" ? "—" : timeText(v.checkOutAt)}</td>
                    <td>
                      {v.status === "checked_in" ? (
                        <RunningTimer visit={v} />
                      ) : (
                        formatMinutes(visitMinutes(v))
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Small read-only list used by supervisors (job popup / dashboards).
export const VisitList = ({ visits = [], showWorker = true, showJob = false }) => {
  const now = useNow(30000, visits.some((v) => v.status === "checked_in"));
  if (!visits.length) return <div className="jci-muted">No check-ins yet.</div>;
  const sorted = [...visits].sort(
    (a, b) => (toDateSafe(a.checkInAt)?.getTime() || 0) - (toDateSafe(b.checkInAt)?.getTime() || 0)
  );
  return (
    <table className="jci-table">
      <thead>
        <tr>
          {showWorker && <th>Worker</th>}
          {showJob && <th>Job</th>}
          <th>Date</th>
          <th>In</th>
          <th>Out</th>
          <th>Time</th>
          <th>Where</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((v) => {
          // Fenced visits are judged by the fence; older ones by distance.
          const far = v.fence
            ? !!v.checkInOutsideFence
            : v.checkInDistanceMeters != null && v.checkInDistanceMeters > FAR_FROM_JOB_METERS;
          return (
            <tr key={v.id}>
              {showWorker && <td>{v.userName || "—"}</td>}
              {showJob && (
                <td>
                  {v.siteName || "Job"}
                  {v.wo ? <div className="jci-muted">WO #{v.wo}</div> : null}
                  {v.checkOutReason && v.checkOutReason !== "user" && (
                    <div className="jci-muted">{checkOutReasonText(v)}</div>
                  )}
                </td>
              )}
              <td>{toDateSafe(v.checkInAt) ? moment(toDateSafe(v.checkInAt)).format("MM/DD") : "—"}</td>
              <td>{timeText(v.checkInAt)}</td>
              <td>
                {v.status === "checked_in" ? <em>on site</em> : timeText(v.checkOutAt)}
                {!showJob && v.checkOutReason === "left_fence" && (
                  <div className="jci-muted">{checkOutReasonText(v)}</div>
                )}
              </td>
              <td>{formatMinutes(visitMinutes(v, now))}</td>
              <td>
                {mapsLinkFor(v.checkInLocation) ? (
                  <a href={mapsLinkFor(v.checkInLocation)} target="_blank" rel="noreferrer">
                    Map
                  </a>
                ) : (
                  "—"
                )}
                {v.checkInDistanceMeters != null && (
                  <span className={far ? "jci-warn" : "jci-muted"}>
                    {" "}
                    {distanceText(v.checkInDistanceMeters)}
                  </span>
                )}{" "}
                <FenceTags visit={v} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// ---------------------------------------------------------------------------
// Staff view inside the job popup: every check-in on this job (all workers).
// Collapsed by default; only listens to Firestore while open.
export const JobVisitHistory = ({ job, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [visits, setVisits] = useState(null);
  const jobId = job?.id;

  useEffect(() => {
    if (!open || !jobId) return undefined;
    const unsub = onSnapshot(
      query(collection(db, JOB_VISITS), where("jobId", "==", jobId)),
      (snap) => setVisits(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        console.warn("Job visit history failed:", err);
        setVisits([]);
      }
    );
    return () => unsub();
  }, [open, jobId]);

  if (!jobId) return null;

  const occ = getVisitOccurrenceKey(job);
  const list = visits || [];
  const thisDay = list.filter((v) => (v.occurrenceDate || "") === occ);
  const shown = thisDay.length ? thisDay : list;
  const totalMins = shown.reduce((sum, v) => sum + visitMinutes(v), 0);

  return (
    <div className="jci-history-box">
      <button type="button" className="jci-link" onClick={() => setOpen((v) => !v)}>
        {open ? "▾" : "▸"} Check-ins
        {visits ? ` (${shown.length}${shown.length ? ` · ${formatMinutes(totalMins)}` : ""})` : ""}
      </button>
      {open && (
        <div style={{ marginTop: 6 }}>
          {visits === null ? (
            <div className="jci-muted">Loading…</div>
          ) : (
            <>
              {thisDay.length === 0 && list.length > 0 && (
                <div className="jci-muted">No check-ins for {occ || "this date"} — showing all dates.</div>
              )}
              <VisitList visits={shown} />
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// "Are you on the property already?"
//
// Opens by itself when someone who should check in opens a job running today
// that they are not checked in to:
//   - not checked in anywhere  -> Yes, check me in / Not yet
//   - checked in at another job -> "You are checked in to <site — scope>.
//     Check out of that job and check in to this one?" Yes / No
// Not yet / No just closes it. It asks again next time the job is opened.
// ---------------------------------------------------------------------------
export const JobOpenCheckInPrompt = ({ job, jobVisits, eligible, isClockedIn }) => {
  const { t } = useLanguage();
  const [dismissedKey, setDismissedKey] = useState(null);
  const [saving, setSaving] = useState(false);

  const checkJob = getCheckInJob(job);
  const key = checkJob?.id ? `${checkJob.id}_${getVisitOccurrenceKey(checkJob)}` : null;

  if (!checkJob || !jobVisits || !eligible || !key) return null;
  if (dismissedKey === key) return null;
  if (!jobVisits.ready) return null;
  if (isJobDoneForCheckIn(checkJob) || !jobRunsToday(checkJob)) return null;
  if (jobVisits.isCheckedInTo(checkJob)) return null;

  const { openVisit } = jobVisits;
  const elsewhere = openVisit && !isSameJobOccurrence(openVisit, checkJob) ? openVisit : null;
  const thisJob = describeVisit(checkJob);

  const close = () => setDismissedKey(key);

  const yes = async () => {
    setSaving(true);
    try {
      // Our popup already asked about switching, so skip the browser confirm.
      await jobVisits.checkIn(checkJob, { confirmed: true });
    } finally {
      setSaving(false);
      setDismissedKey(key);
    }
  };

  return (
    <div className="jci-prompt-overlay" onClick={(e) => e.stopPropagation()}>
      <div className="jci-prompt" role="dialog" aria-modal="true">
        {!isClockedIn ? (
          <>
            <h3>{t("Clock in first")}</h3>
            <p>
              {t("You are not clocked in for the day. Clock in on the Home page, then check in to")}{" "}
              <b>{thisJob}</b> {t("when you are on the property.")}
            </p>
            <div className="jci-prompt-actions">
              <button type="button" className="jci-btn in" onClick={close}>
                {t("OK")}
              </button>
            </div>
          </>
        ) : elsewhere ? (
          <>
            <h3>{t("You are checked in to another job")}</h3>
            <p>
              {t("You are checked in to")} <b>{describeVisit(elsewhere)}</b>.
            </p>
            <p>
              {t("Do you want to check out of that job and check in to")} <b>{thisJob}</b>?
            </p>
            <div className="jci-prompt-actions">
              <button type="button" className="jci-btn in" onClick={yes} disabled={saving}>
                {saving ? t("Switching…") : t("Yes")}
              </button>
              <button type="button" className="jci-btn ghost" onClick={close} disabled={saving}>
                {t("No")}
              </button>
            </div>
          </>
        ) : (
          <>
            <h3>{t("Are you on the property already?")}</h3>
            <p>
              <b>{thisJob}</b>
            </p>
            <p className="jci-muted">{t("Check in when you arrive so your time and location are recorded.")}</p>
            <div className="jci-prompt-actions">
              <button type="button" className="jci-btn in" onClick={yes} disabled={saving}>
                {saving ? t("Getting location…") : t("Yes, check me in")}
              </button>
              <button type="button" className="jci-btn ghost" onClick={close} disabled={saving}>
                {t("Not yet")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Drop-in for job popups outside Home: the Check In / Check Out button plus
// the automatic prompt, for whoever should check in to this job (Techs, or
// anyone on its techAssign).
//
//   const clock = useCheckInClock();
//   const jobVisits = useJobVisits(clock);
//   <JobCheckInPanel job={selectedJob} jobVisits={jobVisits} clock={clock} />
// ---------------------------------------------------------------------------
export const JobCheckInPanel = ({ job, jobVisits, clock, prompt = true }) => {
  if (!job || !jobVisits || !clock?.userId) return null;

  const eligible = clock.role === "Tech" || isUserOnJob(job, clock.userName);
  if (!eligible) return null;

  const checkJob = getCheckInJob(job);
  const done = isJobDoneForCheckIn(checkJob);

  return (
    <>
      {!done && <JobCheckInButton job={checkJob} jobVisits={jobVisits} />}
      {prompt && (
        <JobOpenCheckInPrompt
          job={checkJob}
          jobVisits={jobVisits}
          eligible={eligible}
          isClockedIn={clock.isClockedIn}
        />
      )}
    </>
  );
};

export default JobCheckInButton;
